import { Controller } from "@hotwired/stimulus"
import { countMessage, keyAction, moveIndex, remaining, resolveChoice, suggestUrl } from "../lib/combobox_logic"

// A filter field one types into and picks values from (app/views/shared/_combobox):
// the WAI-ARIA combobox pattern with a listbox popup. Suggestions come from
// GET /suggest/:kind?q= (SuggestionsController) as [{ value, label }]; a pick
// becomes a chip with a hidden field, so the form submits the same parameter
// the fallback field would.
//
// The page works without this controller: the server renders the fallback
// field (checkboxes, a select or a text field) and the combobox hidden, its
// chips' fields disabled. Connecting swaps them: the fallback is hidden and
// disabled, the combobox shown and its fields enabled.
//
// Keys in the field: ↓/↑ open the list and move through it, Enter picks the
// highlighted suggestion (or the one matching what was typed), Escape closes
// the list and then clears the text, Backspace in an empty field removes the
// last chip, Tab closes the list and moves on.
const DEBOUNCE = 120
const BLUR_DELAY = 150

export default class extends Controller {
  static targets = ["fallback", "widget", "label", "input", "listbox", "chips", "status", "template"]
  static values = { url: String, name: String, multiple: Boolean, free: Boolean }

  connect() {
    this.cache = new Map()
    this.options = []
    this.active = -1
    this.fallbackTarget.hidden = true
    this.fallbackTarget.querySelectorAll("input, select, textarea").forEach((field) => { field.disabled = true })
    this.widgetTarget.hidden = false
    this.chipsTarget.querySelectorAll("input[type=hidden]").forEach((field) => { field.disabled = false })
    if (this.hasLabelTarget) this.labelTarget.htmlFor = this.inputTarget.id
    this.closeList()
  }

  disconnect() {
    clearTimeout(this.timer)
    clearTimeout(this.blurTimer)
    this.request?.abort()
  }

  // --- events ------------------------------------------------------------

  search() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.load(), DEBOUNCE)
  }

  open() {
    clearTimeout(this.blurTimer)
    this.load()
  }

  close() {
    this.blurTimer = setTimeout(() => this.closeList(), BLUR_DELAY)
  }

  focusInput(event) {
    if (event.target.closest("button")) return
    this.inputTarget.focus()
  }

  // A click on the list must not blur the field before it picks.
  keepFocus(event) {
    event.preventDefault()
  }

  pick(event) {
    const item = event.target.closest("[role=option]")
    if (!item) return
    const option = this.options[Number(item.dataset.index)]
    if (option) this.choose(option)
  }

  remove(event) {
    event.preventDefault()
    const chip = event.target.closest("[data-chip-value]")
    const label = chip?.querySelector("[data-chip-label]")?.textContent
    chip?.remove()
    this.announce(label ? `Odstránené: ${label}` : "")
    this.inputTarget.focus()
  }

  async keydown(event) {
    const action = keyAction(event.key, {
      open: this.isOpen, active: this.active, query: this.inputTarget.value, chips: this.chipElements.length
    })
    if (!action) return
    if (action !== "dismiss") event.preventDefault()

    switch (action) {
      case "open": await this.load(); this.highlight(moveIndex(-1, event.key === "ArrowUp" ? -1 : 1, this.options.length)); break
      case "next": this.highlight(moveIndex(this.active, 1, this.options.length)); break
      case "prev": this.highlight(moveIndex(this.active, -1, this.options.length)); break
      case "choose": await this.chooseTyped(); break
      case "close": case "dismiss": this.closeList(); break
      case "clear": this.inputTarget.value = ""; break
      case "removeLast": this.removeLast(); break
    }
  }

  // --- suggestions ---------------------------------------------------------

  // Fetches (or reuses) the suggestions for what is typed and shows them.
  async load() {
    clearTimeout(this.timer)
    const query = this.inputTarget.value.trim()
    try {
      const options = this.cache.has(query) ? this.cache.get(query) : await this.fetchOptions(query)
      this.cache.set(query, options)
      if (query !== this.inputTarget.value.trim()) return
      this.loadedQuery = query
      this.render(remaining(options, this.chosenValues))
    } catch (error) {
      if (error.name === "AbortError") return
      this.render([])
      this.announce("Návrhy sa nepodarilo načítať")
    }
  }

  async fetchOptions(query) {
    this.request?.abort()
    this.request = new AbortController()
    const response = await fetch(suggestUrl(this.urlValue, query), {
      headers: { Accept: "application/json" }, credentials: "same-origin", signal: this.request.signal
    })
    if (!response.ok) throw new Error(`suggestions answered ${response.status}`)
    return response.json()
  }

  render(options) {
    this.options = options
    this.active = -1
    this.listboxTarget.replaceChildren(...options.map((option, index) => this.optionElement(option, index)))
    if (options.length === 0) {
      const empty = document.createElement("li")
      empty.className = "lc-option-empty"
      empty.textContent = "Žiadne návrhy"
      this.listboxTarget.append(empty)
    }
    this.listboxTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.announce(countMessage(options.length))
  }

  optionElement(option, index) {
    const item = document.createElement("li")
    item.id = `${this.listboxTarget.id}_${index}`
    item.setAttribute("role", "option")
    item.setAttribute("aria-selected", "false")
    item.dataset.index = index
    item.dataset.value = option.value
    item.className = "lc-option"
    // "6210 Počítačové programovanie": the code in mono, so a column of codes reads at a glance.
    const prefix = `${option.value} `
    if (option.label.startsWith(prefix)) {
      const code = document.createElement("span")
      code.className = "lc-option-code"
      code.textContent = option.value
      // The space keeps the accessible name "6210 Počítačové …"; the flex layout collapses it.
      item.append(code, document.createTextNode(` ${option.label.slice(prefix.length)}`))
    } else {
      item.textContent = option.label
    }
    return item
  }

  highlight(index) {
    this.active = index
    this.listboxTarget.querySelectorAll("[role=option]").forEach((item, position) => {
      item.setAttribute("aria-selected", position === index ? "true" : "false")
      if (position === index) item.scrollIntoView({ block: "nearest" })
    })
    if (index >= 0) {
      this.inputTarget.setAttribute("aria-activedescendant", `${this.listboxTarget.id}_${index}`)
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  closeList() {
    this.listboxTarget.hidden = true
    this.active = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  get isOpen() {
    return !this.listboxTarget.hidden
  }

  // --- chips ---------------------------------------------------------------

  async chooseTyped() {
    if (this.active < 0 && this.loadedQuery !== this.inputTarget.value.trim()) await this.load()
    const option = resolveChoice({
      query: this.inputTarget.value, options: this.options, active: this.active, free: this.freeValue
    })
    if (option) this.choose(option)
  }

  choose(option) {
    if (!this.chosenValues.includes(option.value)) {
      if (!this.multipleValue) this.chipElements.forEach((chip) => chip.remove())
      this.chipsTarget.append(this.chipElement(option))
      this.announce(`Pridané: ${option.label}`)
    }
    this.inputTarget.value = ""
    this.closeList()
    this.inputTarget.focus()
  }

  chipElement(option) {
    const chip = this.templateTarget.content.firstElementChild.cloneNode(true)
    chip.dataset.chipValue = option.value
    const label = chip.querySelector("[data-chip-label]")
    label.textContent = option.label
    label.title = option.label
    chip.querySelector("button").setAttribute("aria-label", `Odstrániť: ${option.label}`)
    const field = chip.querySelector("input[type=hidden]")
    field.name = this.nameValue
    field.value = option.value
    field.disabled = false
    return chip
  }

  removeLast() {
    const chip = this.chipElements.at(-1)
    if (!chip) return
    const label = chip.querySelector("[data-chip-label]")?.textContent
    chip.remove()
    this.announce(`Odstránené: ${label}`)
  }

  get chipElements() {
    return Array.from(this.chipsTarget.querySelectorAll("[data-chip-value]"))
  }

  get chosenValues() {
    return this.chipElements.map((chip) => chip.dataset.chipValue)
  }

  announce(message) {
    this.statusTarget.textContent = message
  }
}
